import { useEffect, useRef } from 'react'

import { ApiError } from 'queries/fetchData'
import { safeTgtLogger } from './safeTgtLogger'

/**
 * Canonical taxonomy of the distinct triggers that lead a user into the
 * Unauthorized experience. This is the single source of truth — downstream
 * work (Firefly trend reporting, differentiated messaging) imports these
 * codes rather than defining its own, so the values stay stable and
 * queryable in TAP.
 */
export const UnauthorizedReason = {
    /** useRestricted denied power-user / beta / Zebra access. */
    APP_RESTRICTED: 'app_restricted',
    /** checkRoutePermissions failed for the resolved route. */
    ROUTE_PERMISSION_DENIED: 'route_permission_denied',
    /**
     * User Details call succeeded but returned an empty response body.
     * Distinct from a failed call: see USER_DETAILS_UPSTREAM_FAILURE and
     * USER_DETAILS_NETWORK_FAILURE for failures.
     */
    NO_USER_DETAILS_DATA: 'no_user_details_data',
    /**
     * User Details call failed upstream: the service (or a dependency)
     * returned a 5xx, or a malformed / non-JSON body. Status is reliably
     * available for these.
     */
    USER_DETAILS_UPSTREAM_FAILURE: 'user_details_upstream_failure',
    /**
     * User Details call never produced an HTTP response: request timed out
     * or the client network failed / went offline. No status is available.
     */
    USER_DETAILS_NETWORK_FAILURE: 'user_details_network_failure',
    /** Requested location is not among the user's entitled locations. */
    LOCATION_MISMATCH: 'location_mismatch',
    /** User Details call returned 403 and the user is forbidden. */
    USER_DETAILS_FORBIDDEN: 'user_details_forbidden',
    /** User Details call returned 401 (service-account / Workday token failure). */
    USER_DETAILS_401: 'user_details_401',
} as const

export type UnauthorizedReasonCode =
    (typeof UnauthorizedReason)[keyof typeof UnauthorizedReason]

/**
 * True when a User Details failure originated upstream (the service or a
 * dependency), rather than from the end user's authorization or the client's
 * network. Covers 5xx responses and malformed / non-JSON bodies.
 *
 * Deliberately branches on error type/status, never on message strings.
 */
export const isUpstreamFailure = (error: unknown): boolean => {
    if (error instanceof ApiError) {
        return error.status >= 500
    }
    // Empty / non-JSON body surfaces as a JSON parse SyntaxError.
    return error instanceof SyntaxError
}

/**
 * True when the User Details call never produced an HTTP response: the
 * request timed out or the client network failed / went offline. These are
 * rethrown unwrapped (not as ApiError) and carry no status.
 */
export const isNetworkFailure = (error: unknown): boolean => {
    if (!(error instanceof Error)) {
        return false
    }
    // AbortController/timeout aborts and DOM timeouts.
    if (error.name === 'TimeoutError' || error.name === 'AbortError') {
        return true
    }
    // fetch() rejects with a TypeError on network/offline failures.
    return error instanceof TypeError
}

/**
 * Non-sensitive diagnostic context attached to an unauthorized log.
 *
 * Intentionally excludes tokens, session credentials, raw upstream response
 * bodies, and the user's AD/greenfield group list. Group membership is
 * represented only as a boolean.
 */
export type UnauthorizedLogContext = {
    /** Raw window.location.pathname the user attempted to reach. */
    attempted_path?: string
    /** Stripped route key used for the permissionsByKPI lookup. */
    denied_route?: string
    /** False when the route has no permissionsByKPI entry (config drift). */
    denied_route_mapped?: boolean
    /** location_id URL param, or the current location short name. */
    requested_location?: string
    /** Whether the user is on a Zebra device. */
    is_zebra?: boolean
    /** Whether the user has any greenfield groups (never the list itself). */
    greenfield_groups_present?: boolean
    /** HTTP status code for User Details failures. Never the response body. */
    upstream_status?: number
}

/** Reasons that represent infra/session failures rather than authz decisions. */
const ERROR_LEVEL_REASONS: readonly UnauthorizedReasonCode[] = [
    UnauthorizedReason.NO_USER_DETAILS_DATA,
    UnauthorizedReason.USER_DETAILS_UPSTREAM_FAILURE,
    UnauthorizedReason.USER_DETAILS_NETWORK_FAILURE,
    UnauthorizedReason.USER_DETAILS_401,
]

const getAppVersion = () =>
    process.env.REACT_APP_VERSION || process.env.APP_VERSION || '0.0.0'

const omitUndefined = (context: UnauthorizedLogContext) =>
    Object.fromEntries(
        Object.entries(context).filter(([, value]) => value !== undefined),
    )

/**
 * Emits a structured OpenTelemetry log describing why the Unauthorized
 * experience was triggered.
 *
 * Uses `warn` for legitimate access denials and `error` for infra/session
 * failures so TAP alerting can distinguish the two.
 */
export const logUnauthorizedReason = (
    reason: UnauthorizedReasonCode,
    context: UnauthorizedLogContext = {},
) => {
    const level = ERROR_LEVEL_REASONS.includes(reason) ? 'error' : 'warn'

    safeTgtLogger[level](`Unauthorized experience shown: ${reason}`, {
        event: {
            action: `unauthorized_${reason}`,
            category: 'authorization',
            outcome: 'failure',
        },
        nonIndexedFields: {
            reason,
            app_version: getAppVersion(),
            ...omitUndefined(context),
        },
    })
}

/**
 * Emits the unauthorized log exactly once per distinct trigger, from an
 * effect rather than during render, so React StrictMode double-renders and
 * incidental re-renders do not produce duplicate events.
 *
 * Pass `null` when no unauthorized condition is active.
 */
export const useLogUnauthorizedReason = (
    reason: UnauthorizedReasonCode | null,
    context: UnauthorizedLogContext = {},
) => {
    const loggedKeyRef = useRef<string | null>(null)
    const contextRef = useRef(context)
    contextRef.current = context

    const key = reason
        ? [
              reason,
              context.attempted_path ?? '',
              context.denied_route ?? '',
              context.requested_location ?? '',
              context.upstream_status ?? '',
          ].join('|')
        : null

    useEffect(() => {
        if (!reason || !key) {
            loggedKeyRef.current = null
            return
        }
        if (loggedKeyRef.current === key) return

        loggedKeyRef.current = key
        logUnauthorizedReason(reason, contextRef.current)
    }, [reason, key])
}
